import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchWorkflow } from '../assets/api.js';
import { getDeal } from '../deals/registry.js';
import { fetchOverrides, putOverride } from './api.js';
import { computeChecklist } from './checklist.js';

const EMPTY = { overrides: [], settings: { stage: 'pre_offer', closingDate: null } };

function remoteOf(ov) {
  return {
    overrides: ov?.overrides || [],
    settings: { stage: ov?.settings?.stage || ov?.stage || 'pre_offer', closingDate: ov?.settings?.closingDate || null },
  };
}

/** Load workflow + overrides, then compute. A missing workflow is an empty inventory, not an error. */
export function useChecklist({ dealId, pkg, diligence, wells, land, map, geoscience }) {
  const [wf, setWf] = useState(null);
  const [remote, setRemote] = useState(EMPTY);
  const [loaded, setLoaded] = useState(false);
  const deal = useMemo(() => getDeal(dealId), [dealId]);

  useEffect(() => {
    let cancel = false;
    setLoaded(false);
    Promise.all([fetchWorkflow(dealId).catch(() => null), fetchOverrides(dealId).catch(() => null)]).then(([workflow, ov]) => {
      if (cancel) return;
      setWf(workflow);
      setRemote(remoteOf(ov));
      setLoaded(true);
    });
    return () => {
      cancel = true;
    };
  }, [dealId]);

  const result = useMemo(() => {
    // A pending fetch is not an empty inventory. Score only after both requests settle.
    if (!loaded) return null;
    return computeChecklist({
      pkg,
      diligence,
      wf,
      wells,
      land,
      map,
      geoscience,
      deal,
      overrides: remote.overrides,
      settings: remote.settings,
    });
  }, [loaded, pkg, diligence, wf, wells, land, map, geoscience, deal, remote]);

  const save = useCallback(
    async (body) => {
      const next = await putOverride(dealId, body);
      setRemote(remoteOf(next));
      return next;
    },
    [dealId],
  );

  return { result, loaded, save, saveSettings: save, wf, deal };
}
